import {
    IExportTemplate,
    IFormTemplate,
    IKnockoutComponent,
    IManualObject,
    IMessage,
    IMessageData,
    IObjectData,
    IObjectType,
    IRouteDefinitionParams,
    ISyncField,
    TFieldType,
    TSyncfield
} from "../../index";
import PromiseRequest from "../../req/PromiseRequest";
import {INPUT_DATE_FORMAT, MAX_RESULTS_PER_PAGE, ObjectTypeId} from "../../constants";
import * as ko from "knockout";
import {ISyncFieldMutable} from "./index";
import {_} from 'lodash';
import {global, lm} from "../../appContext";
import {
    createErrorMessage,
    decodeHtml,
    getObjectDefinition,
    getRawSyncFieldValue,
    getSourceTranslation,
    getSyncField,
    getSyncFieldDisplayName
} from "../../util/misc";
import {KOSubscriptionTracker} from "../../util/KOSubscriptionTracker";
import {convertFromServerString, convertToServerString} from "../../util/datetime";
import {IRequestObject} from "../../req/index";
import {nsSyncFields} from "../../req/nsSyncFields";
import {nsRoute} from "../../util/route";
import {ErrorObject} from "../../error/ErrorObject";
import {ButtonManager} from "../../util/ButtonManager";
import {getCancelButton, getSubmitButton, setRouteHash} from "../../constants/RouteDefinitions";
import {IButton} from "../../index";
import {IComboboxV2Options} from "../combobox/v2";
import {createServiceListPromise} from "../combobox/v2-sources";
import {nsTooltip} from "../../util/nsTooltip";
import {IComboboxOptions_Ruleset} from "../combobox/ruleset";
import {IComboboxOptions_Department, TObjectData_basicDepartment} from "../combobox/department";
import {getSearchParamsFn, searchObjectWithSyncfields} from "../../util/request";
import {IParams_pick_user_any} from "../picker/user-any";
import {nsRuleset} from "../ruleset/nsRuleset";
import {nsObjectDataMutable} from "./nsObjectDataMutable";
import {moment} from 'moment';
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;
import TObjectData_basic = nsSyncFields.TObjectData_basic;
import getSourceToTargetTuples = nsSyncFields.getSourceToTargetTuples;
import mutableHtml from './mutable.html?raw';

export let template = mutableHtml;

export abstract class Mutable extends ParsedRouteDataDecorator {
    private ObjectTypeId = ObjectTypeId;
    protected isReady: KnockoutObservable<boolean> = ko.observable(false);
    protected pageButtons: ButtonManager = new ButtonManager([getCancelButton()].filter((b): b is IButton => b != null))

    protected $original: IManualObject = {
        companyid: '',
        id: 0,
        manual: true,
        orgnr: '',
        syncfields: [],
        type: {id: 0, name: ''} as unknown as IObjectType,
    };

    protected data: KnockoutObservableArray<ISyncFieldMutable> = ko.observableArray<ISyncFieldMutable>([]);
    protected messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    protected _allSyncfields: ISyncField[] = [];
    protected allAvailableSyncfields: ISyncField[] = [];

    protected readonly abstract reqSubmit: PromiseRequest;
    protected reqGetSyncFields!: IRequestObject;

    protected kosub: KOSubscriptionTracker = new KOSubscriptionTracker();

    protected objectId!: IObjectData['id']

    constructor(params: IRouteDefinitionParams) {
        super(params);

        this.pageButtons.add(getSubmitButton({
            onClick: this.onSubmit.bind(this),
            enabled: () => this.isValid() && this.isChanged(),
        }));

        this.kosub.subscribe(this.data, this.updateTabs.bind(this));
        this.updateTabs()
    }

    private updateTabs() {
        const data = this.data();
        const numMandatory = data.filter(o => !!o.syncfield()?.required).length;
        const numOptional = data.filter(o => !o.syncfield()?.required).length;
        this.__tabManager?.getTab('syncfieldsmandatory')?.badge(numMandatory);
        this.__tabManager?.getTab('syncfieldsoptional')?.badge(numOptional);
    }

    private isValid = ko.pureComputed(() => this._isValid());

    protected _isValid(): boolean {
        return this.data().reduce((bool: boolean, obj: ISyncFieldMutable) => obj.errValue.validate() && obj.errSyncfield.validate() && bool, true);
    }

    private isChanged = ko.pureComputed(() => this._isChanged());

    protected _isChanged(): boolean {
        const a = this.getRequiredUpdated().length;
        const b = this.getAdded().length;
        const c = this.getUpdated().length;
        const d = this.getDeleted().length;
        return !!(a || b || c || d);
    }

    private onAddClick() {
        const obj = this.createMutableSyncfield();
        this.data.push(obj);
        this.__tabManager?.getTab('syncfieldsoptional')?.errorManager.addErrorObject(obj.errSyncfield)
        this.__tabManager?.getTab('syncfieldsoptional')?.errorManager.addErrorObject(obj.errValue)
    }

    onDeleteClick(obj: ISyncFieldMutable) {
        const index = this.data.indexOf(obj);
        const undo = () => {
            this.data.splice(index, 0, obj);
            this.__tabManager?.getTab('syncfieldsoptional')?.errorManager.addErrorObject(obj.errSyncfield)
            this.__tabManager?.getTab('syncfieldsoptional')?.errorManager.addErrorObject(obj.errValue)
        };
        global.snackbarData({
            message: `removed synchronization field`,
            undo: undo
        });
        this.__tabManager?.getTab('syncfieldsoptional')?.errorManager.removeErrorObject(obj.errSyncfield)
        this.__tabManager?.getTab('syncfieldsoptional')?.errorManager.removeErrorObject(obj.errValue)
        this.data.remove(obj);
    }

    private getAvailableSyncfields(selected: ISyncFieldMutable): ISyncField[] {
        return this.allAvailableSyncfields.filter((sf: ISyncField): boolean => {
            // always allow itself as an option
            if (selected.syncfield()?.name == sf.name) {
                return true;
            }

            return !this.data()
                .filter((sfm: ISyncFieldMutable) => !!sfm.syncfield()) // we only care about syncfields that are populated
                .find((sfm: ISyncFieldMutable) => sfm.syncfield()!.name == sf.name)
        });
    }

    private usedSyncFields = ko.pureComputed((): ISyncField[] => {
        return this.data()
            .map(msf => msf.syncfield())
            .filter((sf): sf is ISyncField => !!sf);
    })

    protected onSubmit() {
        const data = this.getSubmitData();
        const json = JSON.stringify(data);

        this.messageData({message: this.getEventMessage('submit')});
        this.isReady(false);

        this.reqSubmit.abort();
        this.reqSubmit.request({jobject: json})
            .then(this.onSubmitSuccess.bind(this))
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)));
                this.isReady(true);
            });
    }

    private onSubmitSuccess(data: IObjectData[]) {
        const object = data[0];
        if (!object) {
            return;
        }
        this.$original = object;
        global.cacheManager.removeCache('getruleset');
        global.cacheManager.removeCache('searchruleset');
        global.cacheManager.removeCache("searchobject");
        global.cacheManager.removeCache("getobject");
        global.snackbarData({message: this.getEventMessage('success')});
        this.objectId = object.id;
        setRouteHash(this.__objDef!.routeDetailsManual!, {id: this.objectId})
    }

    protected getSubmitData(): IManualObject {
        const orgnr = global.login.selectedOrganizationId();
        return _.extend({}, this.$original, {
            companyid: this.$original.companyid || orgnr,
            orgnr: this.$original.orgnr || orgnr,
            syncfields: this.getSyncFieldSubmitData(),
        });
    }

    protected getSyncFieldSubmitData(): ISyncField[] {
        const required = this.getRequired();
        const added = this.getAdded();
        const updated = this.getUpdated();
        const deleted = _.cloneDeep(this.getDeleted());
        deleted.forEach((d: ISyncField): void => {
            Object.assign(d, {value: null as unknown as string});
        });
        return ([] as ISyncField[]).concat(required, added, updated, deleted);
    }

    private getRequiredUpdated(): ISyncField[] {
        const original = this.$original.syncfields;
        const current: ISyncField[] = this.data().map(this.mutableToStatic.bind(this));
        return current.filter((c: ISyncField) => {
            const syncField = getSyncField(c.name, original);
            return c.required && syncField != null && c.value != syncField.value;
        });
    }

    private getRequired(): ISyncField[] {
        const current: ISyncField[] = this.data().map(this.mutableToStatic.bind(this));
        return current.filter((c: ISyncField) => c.required);
    }

    private getAdded(): ISyncField[] {
        const original = this.$original.syncfields;
        const current: ISyncField[] = this.data().map(this.mutableToStatic.bind(this));
        return current.filter((cu: ISyncField) => {
            if (cu.required) {
                return false;
            }
            const or = getSyncField(cu.name, original);
            const a = !!or && or.value;
            const b = !!cu && cu.value;
            return !a && b;
        });
    }

    private getUpdated(): ISyncField[] {
        const original = this.$original.syncfields.map(sf => {
            let value: string

            // the object's syncfield could be lacking some definitions - let's find the prototype syncfield so we can
            // check it's field type
            const _sf = this._allSyncfields.find(_sf => _sf.name == sf.name);
            if (sf.value && _sf?.fieldtype && this.isSyncFieldDate(_sf.fieldtype)) {
                value = convertToServerString(convertFromServerString(sf.value)) ?? ''
            } else {
                value = sf.value ?? ''
            }

            return _.defaults({value: value}, sf);

        });
        const current: ISyncField[] = this.data().map(this.mutableToStatic.bind(this));
        return current.filter((_current: ISyncField) => {
            if (_current.required) {
                return false;
            }
            const _original = getSyncField(_current.name, original);
            if (!_current || !_original) {
                return false;
            }
            return _original.value != _current.value;
        });
    }

    private isUpdated(mutable: ISyncFieldMutable): boolean {
        const original = this.$original.syncfields.find(o => o.name == mutable.syncfield()?.name)
        const current = this.mutableToStatic(mutable)
        return original?.value != current.value
    }

    private getDeleted(): ISyncField[] {
        const original = this.$original.syncfields;
        const current: ISyncField[] = this.data().map(this.mutableToStatic.bind(this));
        return original.filter((sf: ISyncField) => this.isSyncfieldMutable(sf) && sf.value && !getSyncField(sf.name, current));
    }

    protected mutableToStatic(mutable: ISyncFieldMutable): ISyncField {
        let value = mutable.value();
        if (this.isSyncFieldDate(mutable.syncfield()?.fieldtype)) {
            value = convertToServerString(moment(mutable.dateValue(), INPUT_DATE_FORMAT)) ?? '';
        }
        return _.defaults({value: value}, mutable.syncfield());
    }

    private getSyncfieldName(mutable: ISyncFieldMutable): string {
        return getSourceTranslation(this.mutableToStatic(mutable))
    }

    protected isSyncFieldDate(fieldType: TFieldType | undefined): boolean {
        return fieldType == 'DateTime';
    }

    protected get objectTypeId(): ObjectTypeId {
        return this.__objectTypeId!;
    }

    protected fetchSyncFields(): Promise<ISyncField[]> {
        if (!this.reqGetSyncFields) {
            this.reqGetSyncFields = nsSyncFields.getSyncFields(this.objectTypeId);
        }
        return this.reqGetSyncFields.request;
    }

    protected onAllSyncFields(syncfields: ISyncField[]) {
        this._allSyncfields = syncfields
        this.allAvailableSyncfields = this.sortSyncFields(syncfields)
            .filter(sf => this.isSyncfieldMutable(sf))
    }

    protected sortSyncFields(syncfields: ISyncField[]): ISyncField[] {
        return _(syncfields)
            .sortBy((sf: ISyncField) => sf.name.toLowerCase())
            .sortBy((sf: ISyncField) => !sf.required) // put the required first
            .value();
    }

    protected abstract prepareObservableSyncFields(): void;

    protected createMutableFromSyncField(syncField: ISyncField): ISyncFieldMutable {
        const ret = this.createMutableSyncfield();
        const sf = this._allSyncfields.find((_sf: ISyncField) => _sf.name == syncField.name)
        if (!sf) {
            return ret;
        }
        ret.syncfield(sf);
        if (this.isSyncFieldDate(sf.fieldtype)) {
            const m = convertFromServerString(syncField.value);
            const serverString = convertToServerString(m);
            ret.value(serverString ?? '');
            ret.dateValue(m.format(INPUT_DATE_FORMAT));
        } else {
            ret.value(syncField.value);
        }
        return ret;
    }

    private createMutableSyncfield(): ISyncFieldMutable {
        const syncfield: ISyncFieldMutable['syncfield'] = ko.observable(null);
        const value: ISyncFieldMutable['value'] = ko.observable("");
        const errSyncfield: ISyncFieldMutable['errSyncfield'] = new ErrorObject()
        const errValue: ISyncFieldMutable['errValue'] = new ErrorObject()
        const dateValue = ko.observable(null);
        const useComponent: KnockoutObservable<boolean> = ko.observable(false) // TODO should be true by default if syncfield allows for component

        errSyncfield.addValidator({
            fn: () => !!syncfield(),
            message: 'invalid synchronization field',
        })

        errValue.addValidator({
            fn: () => {
                const sf = syncfield();
                if (!sf) {
                    return true;
                }
                if (sf.fieldtype == "DateTime") {
                    return !!dateValue()
                }
                return !!value()
            },
            message: 'invalid value',
        })

        return {
            syncfield,
            value,
            dateValue,
            errSyncfield,
            errValue,
            useComponent,
        }
    }

    private getDepartmentText(obj: IObjectData) {
        return getSyncFieldDisplayName(obj.type.id, obj.syncfields);
    }

    private getRawSyncFieldValue(key: TSyncfield | TSyncfield[], obj: IObjectData) {
        return getRawSyncFieldValue(key, obj.syncfields);
    }

    private getComponent(obj: ISyncFieldMutable): IKnockoutComponent | undefined {
        const selectedSyncfield = obj.syncfield();
        if (!selectedSyncfield) {
            return undefined;
        }
        const syncfieldName = selectedSyncfield.name;

        switch (syncfieldName) {
            case 'ManualMerge': {
                const val = obj.value();
                if (['', 'true', 'false'].includes(val?.toLowerCase())) {
                    return {
                        name: 'component/object/syncfield/boolean',
                        params: {
                            val: obj.value
                        }
                    }
                }
            }
        }

        const isRuleset = ['AvailableToRuleSetId', 'ManagedByRuleSetId', 'MemberRuleSetId'].includes(syncfieldName);
        if (isRuleset) {
            const input = ko.observable('')
            const params: IComboboxOptions_Ruleset = {
                comboboxOptions: {
                    inputAttrs: {placeholder: lm.t('choose ruleset|td')},
                    initialMatch: o => o.id.toString() == obj.value(),
                    onInitialMatchResolved: nsObjectDataMutable.comboboxOnInitialObjectFn(obj.value()),
                    input,
                    onSelect: (o) => obj.value(o?.id?.toString() ?? input()),
                    inputStatus: nsObjectDataMutable.comboboxInputStatus,
                    title: nsObjectDataMutable.comboboxTitleFn.bind(this, nsTooltip.getTitle_ruleset),
                },
                objectTypeIds: ObjectTypeId.User,
                showIcon: true,
            };
            return {
                name: 'component/combobox/ruleset',
                params: params,
            }
        }

        const isDepartment = ['Department', 'DepartmentNumber', 'ManagerOf', 'ParentDepartment', 'ParentDepartmentNumber'].includes(syncfieldName);
        if (isDepartment) {
            let v: ((o: TObjectData_basicDepartment | null, s: string) => string) | undefined
            switch (this.objectTypeId) {
                case ObjectTypeId.FormData:
                case ObjectTypeId.User: {
                    switch (syncfieldName) {
                        case 'Department':
                        case 'ParentDepartment': {
                            v = (o, s) => o ? (getRawSyncFieldValue('Department', o.syncfields) ?? s) : s
                            break
                        }
                        case 'DepartmentNumber':
                        case 'ManagerOf':
                        case 'ParentDepartmentNumber': {
                            const tuples = nsSyncFields.getSourceToTargetTuples({
                                srcObjectTypeId: ObjectTypeId.User,
                                trgObjectTypeId: ObjectTypeId.Department,
                                key: syncfieldName,
                            })[0];
                            if (!tuples) {
                                break;
                            }
                            const tupleKey = tuples[3]
                            if (tupleKey) {
                                v = (o, s) => o ? (getRawSyncFieldValue(tupleKey, o.syncfields) ?? s) : s
                            }
                            break
                        }
                    }
                    break;
                }
                case ObjectTypeId.Department: {
                    switch (syncfieldName) {
                        case 'ParentDepartmentNumber': {
                            v = (o, s) => o ? (getRawSyncFieldValue('DepartmentNumber', o.syncfields) ?? s) : s
                            break
                        }
                    }
                    break;
                }
            }
            if (!v) {
                return undefined
            }
            const resolveValue = v;
            const input = ko.observable('')
            const params: IComboboxOptions_Department = {
                comboboxOptions: {
                    inputAttrs: {placeholder: lm.t('choose subject|s:((department|ac))|td')},
                    initialMatch: o => resolveValue(o, '') == obj.value(),
                    onInitialMatchResolved: nsObjectDataMutable.comboboxOnInitialObjectFn(obj.value()),
                    option: {
                        tooltip: (o) => nsObjectDataMutable.comboboxTitleFn(nsTooltip.getTitle_objectdata, o, ''),
                    },
                    input,
                    onSelect: (o) => obj.value(resolveValue(o, input())),
                    inputStatus: nsObjectDataMutable.comboboxInputStatus,
                    title: nsObjectDataMutable.comboboxTitleFn.bind(this, nsTooltip.getTitle_objectdata),
                },
                showIcon: true,
                showRoot: ['ParentDepartment', 'ParentDepartmentNumber'].includes(syncfieldName),
            }
            return {
                name: 'component/combobox/department',
                params: params,
            }
        }

        const isMessageTemplate = ['ApplicationMessageId', 'RejectionMessageId'].includes(syncfieldName);
        if (isMessageTemplate) {
            const input = ko.observable('')
            const listSource = createServiceListPromise<IMessage>({
                name: 'searchmessage',
                params: getSearchParamsFn('searchmessage')({resultsprpage: MAX_RESULTS_PER_PAGE}),
            })
            const params: IComboboxV2Options<IMessage> = {
                inputAttrs: {placeholder: lm.t('choose subject|s:((messageflow|ac))|td')},
                initialMatch: o => o.id.toString() == obj.value(),
                option: {
                    label: o => o.name,
                    tooltip: (o) => nsObjectDataMutable.comboboxTitleFn(nsTooltip.getTitle_messageflow, o, ''),
                },
                onInitialMatchResolved: nsObjectDataMutable.comboboxOnInitialObjectFn(obj.value()),
                input,
                onSelect: (o) => obj.value(o?.id?.toString() ?? input()),
                promise: listSource.promise,
                onDispose: listSource.onDispose,
                inputStatus: nsObjectDataMutable.comboboxInputStatus,
                title: nsObjectDataMutable.comboboxTitleFn.bind(this, nsTooltip.getTitle_messageflow),
            };
            return {
                name: 'component/combobox/icon-select',
                params: {
                    icon: 'fa-message-flow',
                    comboboxParams: params,
                },
            }
        }

        const isSyncTemplate: boolean = ['Parent'].includes(syncfieldName)
        if (isSyncTemplate) {
            switch (this.objectTypeId) {
                case ObjectTypeId.Group: {
                    const input = ko.observable('')
                    const listSource = createServiceListPromise<IExportTemplate>({
                        name: 'searchexporttemplate',
                        params: getSearchParamsFn('searchexporttemplate')({resultsprpage: MAX_RESULTS_PER_PAGE}),
                    })
                    const params: IComboboxV2Options<IExportTemplate> = {
                        inputAttrs: {placeholder: lm.t('choose subject|s:((synchronization template|ac))|td')},
                        initialMatch: o => o.id.toString() == obj.value(),
                        option: {
                            label: o => `${o.name} (${o.id})`,
                            tooltip: (o) => nsObjectDataMutable.comboboxTitleFn(nsTooltip.getTitle_exportTemplate, o, ''),
                        },
                        onInitialMatchResolved: nsObjectDataMutable.comboboxOnInitialObjectFn(obj.value()),
                        input,
                        onSelect: (o) => obj.value(o?.id?.toString() ?? input()),
                        promise: listSource.promise,
                        onDispose: listSource.onDispose,
                        inputStatus: nsObjectDataMutable.comboboxInputStatus,
                        title: nsObjectDataMutable.comboboxTitleFn.bind(this, nsTooltip.getTitle_exportTemplate),
                    }
                    return {
                        name: 'component/combobox/icon-select',
                        params: {
                            icon: 'fa-sync-template',
                            comboboxParams: params,
                        },
                    }
                }
            }
        }

        const isFormTemplate: boolean = ['FormId', 'FormName'].includes(syncfieldName)
        if (isFormTemplate) {
            let v: ((o: IFormTemplate | null, s: string) => number | string) | undefined
            switch (this.objectTypeId) {
                case ObjectTypeId.FormData: {
                    switch (syncfieldName) {
                        case 'FormId': {
                            v = (o, s) => o ? o.id : s
                            break
                        }
                        case 'FormName': {
                            v = (o, s) => o ? o.name : s
                            break
                        }
                    }
                    if (!v) {
                        return undefined
                    }
                    const resolveFormValue = v;
                    const input = ko.observable('')
                    const listSource = createServiceListPromise<IFormTemplate>({
                        name: 'searchformtemplate',
                        params: getSearchParamsFn('searchformtemplate')({resultsprpage: MAX_RESULTS_PER_PAGE}),
                    })
                    const params: IComboboxV2Options<IFormTemplate> = {
                        inputAttrs: {placeholder: lm.t('choose subject|s:((form template|ac))|td')},
                        initialMatch: o => String(resolveFormValue(o, '')) == obj.value(),
                        option: {
                            label: o => decodeHtml(o.name),
                            tooltip: (o) => nsObjectDataMutable.comboboxTitleFn(nsTooltip.getTitle_formtemplate, o, ''),
                        },
                        onInitialMatchResolved: nsObjectDataMutable.comboboxOnInitialObjectFn(obj.value()),
                        input,
                        onSelect: (o) => obj.value(String(resolveFormValue(o, input()))),
                        promise: listSource.promise,
                        onDispose: listSource.onDispose,
                        inputStatus: nsObjectDataMutable.comboboxInputStatus,
                        title: nsObjectDataMutable.comboboxTitleFn.bind(this, nsTooltip.getTitle_formtemplate),
                    }
                    return {
                        name: 'component/combobox/icon-select',
                        params: {
                            icon: 'fa-form-template',
                            comboboxParams: params,
                        },
                    }
                }
            }
        }

        if (syncfieldName == 'Manager') {
            const tuples = getSourceToTargetTuples({
                srcObjectTypeId: ObjectTypeId.Department,
                trgObjectTypeId: ObjectTypeId.User,
                key: 'Manager',
            })[0];
            if (!tuples) {
                return;
            }
            const key = tuples[3]!;
            const params: IParams_pick_user_any<TObjectData_basic> = {
                input: obj.value,
                onSelect: o => {
                    if (!o) {
                        return;
                    }
                    obj.value(getRawSyncFieldValue(key, o.syncfields) ?? '')
                },
                dialogPickObjectParams: {
                    syncfieldNames: [key],
                },
                promiseInitial: (): Promise<TObjectData_basic> => obj.value()
                    ? searchObjectWithSyncfields(ObjectTypeId.User, [key, ...(getObjectDefinition(ObjectTypeId.User)?.syncfieldDisplayName ?? [])], {
                        jruleset: JSON.stringify(nsRuleset.generateRuleset({
                            rules: [
                                nsRuleset.generateRule({
                                    attribute: key,
                                    argumentoperator: 'Er lik',
                                    argument: obj.value(),
                                }),
                            ]
                        })),
                        resultsprpage: 1,
                    }).then((data: IObjectData[] | string[][]) => (data as IObjectData[])?.[0] as TObjectData_basic)
                    : Promise.resolve(null as unknown as TObjectData_basic),
            };
            return {
                name: 'component/picker/user-any',
                params: params,
            }
        }
        return undefined;
    }

    private showOptionalSyncfields = ko.pureComputed((): boolean => {
        return !!this.data().filter(o => !o.syncfield()?.required).length;
    })

    private getComboboxParams_syncfield(obj: ISyncFieldMutable): IComboboxV2Options<ISyncField> {
        const used_names = ko.pureComputed(() => this.data().filter(o => o.syncfield()).map(o => o.syncfield()?.name))
        return {
            prepareData: {
                filter: o => o.objecttypeid == this.objectTypeId,
            },
            inputAttrs: {placeholder: lm.t('synchronization field')},
            data: this.allAvailableSyncfields,
            initialMatch: o => obj.syncfield()?.name == o.name,
            option: {
                constrain: o => !used_names().includes(o.name) || obj.syncfield()?.name == o.name,
                label: o => getSourceTranslation(o),
                tooltip: o => o ? (lm.t(nsTooltip.getTitle_syncfield(o) ?? '') ?? '') : '',
            },
            selected: obj.syncfield as KnockoutObservable<ISyncField | null>,
            reloadWhen: global.sourceTranslation,
            inputStatus: (selected) => selected ? 'ok' : 'warning',
        }
    }

    protected isSyncfieldMutable(syncfield: ISyncField) {
        const sf = this._allSyncfields.find(o => o.name == syncfield.name);
        return !!sf && nsSyncFields.isSyncfieldMutable(sf)
    }

    override dispose() {
        super.dispose();
        this.kosub.unsuscribeAll();
        this.reqGetSyncFields?.promise?.abort();
    }

    protected abstract getEventMessage(eventType: 'submit' | 'success'): string
}
